/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import _classNames from 'classnames';
import _get from 'lodash/get';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { getImageFile } from 'utils/stringUtils';
import { getColorsConfig } from 'containers/Assessments/utils';
import { useHistory, useLocation } from 'react-router-dom';
import ButtonBase from '@material-ui/core/ButtonBase';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { useSelector } from 'react-redux';
import { makeSelectSiteCopyInOtherLang } from 'containers/Main/selectors';
import PageContext from '../PageContext';
import getServiceType from '../utils/getServiceType';
import getFormattedType from '../utils/getFormattedType';
import { getHeroResourceTitle } from '../utils';

const useStyles = makeStyles(theme => ({
  container: {
    width: props => (props.fullwidth ? '100%' : '95%'),
    display: 'block',
    [theme.breakpoints.down('sm')]: {
      width: '100% !important',
    },
    marginTop: '10px',
    height: ({ isMobile }) => isMobile && '300px',
  },
  cardRoot: {
    boxShadow: 'none !important',
    borderRadius: ({ isMobile }) => (isMobile ? 0 : 5),
    height: ({ isMobile }) => (isMobile ? 432 : 170),
    textAlign: 'center',
    position: 'relative',
    backgroundColor: 'transparent',
    zIndex: 1,
    display: ({ isMobile }) => (isMobile ? 'inline-grid' : 'flex'),
    flexDirection: 'row',
    width: props => props.width || '100%',
    marginBottom: props => props.mb || 0,
    marginTop: props => props.mt || 0,
    [theme.breakpoints.down('sm')]: {
      width: '100% !important',
      marginTop: '0 !important',
    },
  },
  time: {
    height: 21,
    width: 55,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    lineHeight: '18px',
    background: theme.palette.primary.main,
    color: '#FFFFFF',
    borderRadius: 2,
    position: 'absolute',
    top: 10,
    left: 11,
    zIndex: 99,
  },
  info: {
    height: ({ isMobile }) => (isMobile ? '53%' : '100%'),
    padding: '8px 10px',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    background: ({ color }) => color.main,
    borderTopRightRadius: ({ isMobile }) => (isMobile ? 0 : 5),
    borderBottomRightRadius: ({ isMobile }) => (isMobile ? 0 : 5),
  },
  link: {
    fontSize: '12px !important',
    display: 'flex',
    lineHeight: '16px !important',
    alignItems: 'center',
    color: ({ color }) => color.font,
    textDecoration: 'none',
    justifyContent: 'flex-start',
    fontFamily: 'MadaBold !important',
    '& img': {
      marginLeft: 5,
    },
    marginTop: '30px',
  },
  mobileName: {
    marginTop: 12,
    display: '-webkit-box',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: 14,
  },
  name: {
    display: '-webkit-box',
    '-webkit-line-clamp': ({ isTablet }) => (isTablet ? '2' : '3'),
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: ({ color }) => color.font,
    fontSize: ({ isTablet }) => (isTablet ? 15 : 20),
    lineHeight: '28px',
    marginTop: 33,
  },
  type: {
    fontSize: 12,
  },
  icon: {
    height: 13,
    marginRight: 7,
  },
  cardImg: {
    width: ({ isMobile, isTablet }) =>
      isMobile ? '100%' : isTablet ? '91%' : '53%',
  },
  grayscale: {
    filter: 'grayscale(1)',
  },
}));

function AssessmentCard({
  data,
  clickHandler,
  imageHeight,
  width,
  mb,
  mt,
  fullwidth = false,
  locale,
  otherLanguage,
  isMobile,
  isTablet,
}) {
  const history = useHistory();
  const location = useLocation();
  const [siteCopy] = useSiteCopySelector(['resource-item-page']);
  const { landingInfo } = useContext(PageContext);
  const siteCopyInOtherLang = useSelector(makeSelectSiteCopyInOtherLang());
  const imageSrc = getImageFile(data);
  const { slug, name, time, title } = data;
  const routeType = getServiceType(data);
  const type = getFormattedType(data);
  const heroResourceTitle = getHeroResourceTitle({
    type,
    name,
    title,
  });
  const { color, takeArrowSvg } = getColorsConfig(data);
  const localeParam = locale ? `?lang=${locale}` : '';
  const link = `/${routeType}/${slug}${localeParam}`;
  const classes = useStyles({
    color,
    width,
    mb,
    mt,
    fullwidth,
    isMobile,
    isTablet,
  });

  const shouldApplyGrayscaleFilter = _get(
    landingInfo,
    'pageText.grayscale',
    false,
  );
  const cardRootStyle = imageSrc
    ? {
        backgroundImage: `url(${imageSrc})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        width: '100%',
        height: imageHeight || '100%',
        borderTopLeftRadius: isMobile ? 0 : 5,
        borderBottomLeftRadius: isMobile ? 0 : 5,
      }
    : {};
  const redirect = () => {
    const pageName = location.pathname.split('/')[1];
    const page = pageName ? `${pageName}Page` : 'homePage';

    clickHandler('Resource Card - Clicked', {
      name: heroResourceTitle,
      type,
      slug,
      page,
      path: 'banner',
    });
    history.push(link);
  };
  return (
    <ButtonBase disableRipple onClick={redirect} className={classes.container}>
      <div
        className={_classNames(classes.cardRoot, {
          [classes.grayscale]: shouldApplyGrayscaleFilter,
        })}
      >
        {imageSrc && (
          <div className={classes.cardImg}>
            <div style={cardRootStyle} />
          </div>
        )}
        {time && (
          <Typography className={classes.time}>{`${time} ${
            time > 1 ? 'MINS' : 'MIN'
          }`}</Typography>
        )}
        <div className={classes.info}>
          <Typography variant="h5" align="left" className={classes.name}>
            {heroResourceTitle}
          </Typography>
          <Typography className={classes.link}>
            {_get(
              otherLanguage ? siteCopyInOtherLang : siteCopy,
              'pageCopy.takeAssessmentLabel',
            )}
            <img src={takeArrowSvg} alt="" />
          </Typography>
        </div>
      </div>
    </ButtonBase>
  );
}

AssessmentCard.propTypes = {
  data: PropTypes.object,
  clickHandler: PropTypes.func,
};

export default AssessmentCard;
